import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LitPage } from '../components/lit-page';
import { isPuzzleId, type NavTarget, type PuzzleId } from '../components/luna-common';
import type { IconName } from '../components/luna-icon/icon-registry';
import { navigate } from '../navigation';
import { resolveNavItems, resolveShellVariant, type ShellVariant } from '../shell';
import { getCurrentUser, logout } from '../auth/session';
import { getPuzzle } from '../puzzles/puzzleBase';

import '../components/luna-frame/luna-frame';
import '../components/luna-top-bar/luna-top-bar';
import '../components/luna-nav/luna-nav';
import '../components/luna-back-chip/luna-back-chip';
import '../components/luna-status-chip/luna-status-chip';
import '../components/luna-howto-overlay/luna-howto-overlay';
import '../components/luna-icon/luna-icon';

const NAV_LABELS: Record<NavTarget, string> = {
  home: 'Home',
  programme: 'My programme',
  appearance: 'Appearance',
};

@customElement('app-shell')
export class AppShell extends LitPage {
  @state() private _variant: ShellVariant = 'none';
  @state() private _path = '/';
  @state() private _statusLabel = '';
  @state() private _backLabel = 'Home';
  @state() private _howtoOpen = false;
  @state() private _howtoTitle = '';
  @state() private _howtoGoal = '';
  @state() private _howtoInstructions: string[] = [];
  @state() private _sessionLabel = 'Log in';
  @state() private _sessionIcon: IconName = 'login';

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('vaadin-router-location-changed', this.onLocationChanged);
    window.addEventListener('luna-howto', this.onHowto);
    window.addEventListener('luna-status-chip', this.onStatusChip);
    this.onLocationChanged();
  }

  disconnectedCallback() {
    window.removeEventListener('vaadin-router-location-changed', this.onLocationChanged);
    window.removeEventListener('luna-howto', this.onHowto);
    window.removeEventListener('luna-status-chip', this.onStatusChip);
    super.disconnectedCallback();
  }

  private onLocationChanged = () => {
    const path = window.location.pathname || '/';
    this._path = path;
    this._variant = resolveShellVariant(path);
    this._statusLabel = '';
    this._backLabel = this.resolveBackLabel();
    this._sessionLabel = getCurrentUser() ? 'Log out' : 'Log in';
    this._sessionIcon = getCurrentUser() ? 'logout' : 'login';
  };

  private onStatusChip = (event: Event) => {
    const detail = (event as CustomEvent<{ label?: string; path?: string }>).detail;
    // Reject stale emits (e.g. a late setPreset from an already-left gameplay page).
    if (detail?.path !== undefined && detail.path !== this._path) {
      return;
    }
    if (typeof detail?.label === 'string') {
      this._statusLabel = detail.label;
    }
  };

  // The chip says where it goes: 'Home' when it lands on the homescreen,
  // 'Back to XXX' otherwise (board selection, programme).
  private resolveBackLabel(): string {
    if (this._variant === 'puzzle-bar') {
      const params = new URLSearchParams(window.location.search);
      return params.get('from') === 'programme' ? 'Back to Programme' : 'Back to Selection';
    }
    return 'Home';
  }

  private onBackClick(): void {
    if (this._variant === 'puzzle-bar') {
      const match = /^\/puzzles\/([^/]+)/.exec(this._path);
      if (match) {
        const puzzleName = match[1];
        // Programme flow is explicit via ?from=programme (level URLs are used by free play too).
        const params = new URLSearchParams(window.location.search);
        if (params.get('from') === 'programme') {
          navigate('/programme');
        } else {
          navigate(`/puzzles/${puzzleName}/boards`);
        }
        return;
      }
    }
    navigate('/');
  }

  private onHowto = (event: Event) => {
    const detail = (event as CustomEvent<{ puzzle?: string }>).detail;
    const puzzle = detail?.puzzle;
    if (typeof puzzle === 'string' && isPuzzleId(puzzle)) {
      void this.openHowto(puzzle);
    }
  };

  private async openHowto(puzzleName: PuzzleId): Promise<void> {
    const puzzle = await getPuzzle(puzzleName);
    this._howtoTitle = `How to play — ${puzzle.name}`;
    this._howtoGoal = puzzle.goal;
    this._howtoInstructions = puzzle.instructions;
    this._howtoOpen = true;
  }

  private onHowtoClose(): void {
    this._howtoOpen = false;
  }

  private onSessionClick(): void {
    if (getCurrentUser()) {
      void logout();
    } else {
      navigate('/login');
    }
  }

  private get currentNav(): NavTarget {
    return this._path === '/programme' ? 'programme' : 'home';
  }

  private onNavNavigate(event: CustomEvent<{ target: NavTarget }>): void {
    const target = event.detail?.target;
    switch (target) {
      case 'home':
        navigate('/');
        break;
      case 'programme':
        navigate('/programme');
        break;
      case 'appearance':
        navigate('/appearance');
        break;
      default:
        break;
    }
  }

  content() {
    const current = this.currentNav;

    return html`
      <div class="app-shell app-shell--${this._variant}">
        <luna-frame>
          <luna-top-bar slot="bar" variant=${this._variant === 'home-nav' ? 'home' : 'branded'}>
            <!-- The label is wrapped in a span so light-DOM distribution moves
                 the element (with Lit's part anchors inside it) as one unit —
                 a bare text node is torn from its anchors and the binding
                 goes stale (shows the previous page's label). -->
            <luna-back-chip slot="lead" @click=${this.onBackClick}
              ><span>${this._backLabel}</span></luna-back-chip
            >
            <luna-status-chip
              slot="trail"
              label=${this._statusLabel}
              ?hidden=${!this._statusLabel}
            ></luna-status-chip>
            <luna-nav
              slot="trail"
              .items=${resolveNavItems()}
              current=${current}
              @luna-navigate=${this.onNavNavigate}
            ></luna-nav>
            <span slot="trail" class="app-shell__current-indicator" aria-current="page">
              <span class="mbw-type-label-nav">${NAV_LABELS[current]}</span>
            </span>
            <button
              slot="trail"
              type="button"
              class="app-shell__session mbw-type-label-nav"
              aria-label=${this._sessionLabel}
              @click=${this.onSessionClick}
            >
              <span class="app-shell__session-label" aria-hidden="true">${this._sessionLabel}</span>
              <luna-icon
                class="app-shell__session-icon"
                name=${this._sessionIcon}
                size=${22}
              ></luna-icon>
            </button>
          </luna-top-bar>
          <div class="app-shell__outlet"></div>
        </luna-frame>

        <luna-howto-overlay
          title=${this._howtoTitle}
          goal=${this._howtoGoal}
          .open=${this._howtoOpen}
          .instructions=${this._howtoInstructions}
          @luna-close=${this.onHowtoClose}
        ></luna-howto-overlay>
      </div>
    `;
  }

  styles() {
    return css`
      .app-shell {
        min-height: 100dvh;
        background: var(--mbw-os-bg);
        display: flex;
        justify-content: center;
        align-items: stretch;
      }

      /* BR-103: the luna-frame ELEMENT is a flex item of .app-shell — without an
         explicit width it shrink-to-fits its content (capped at 1180px by the
         body's max-width) and gets centered by justify-content, so the bar never
         goes full-bleed. The element itself must be given the full width. */
      .app-shell luna-frame {
        display: block;
        width: 100%;
      }

      .app-shell .luna-frame {
        min-height: 100dvh; /* BR-103: grows with content — scrolling is document-level */
      }

      /* Shell none: legacy pages render bare, exactly as before U007. */
      .app-shell--none {
        background: transparent;
      }

      .app-shell--none .luna-frame {
        width: 100%;
        max-width: 100%;
        height: auto;
        min-height: 100dvh;
        background: transparent;
        box-shadow: none;
        border-radius: 0;
        overflow: visible;
      }

      .app-shell--none .luna-frame__bar {
        display: none;
      }

      .app-shell--none .luna-frame__body {
        overflow: visible;
      }

      /* Chrome children are always rendered (stable DOM for the router outlet)
         and revealed per-variant/breakpoint so lit never has to remove them. */
      .app-shell luna-top-bar {
        display: block;
      }

      .app-shell--none luna-top-bar {
        display: none;
      }

      .app-shell luna-back-chip,
      .app-shell luna-status-chip,
      .app-shell luna-nav,
      .app-shell .app-shell__current-indicator,
      .app-shell .app-shell__session {
        display: none;
      }

      .app-shell--home-nav .app-shell__session,
      .app-shell--picker-back .app-shell__session,
      .app-shell--puzzle-bar .app-shell__session {
        display: inline-flex;
      }

      .app-shell__session {
        align-items: center;
        height: 46px;
        padding: 0 20px;
        border: none;
        border-radius: var(--luna-radius-btn-sm);
        background: transparent;
        color: var(--mbw-onbar-text);
        opacity: 0.9;
        cursor: pointer;
      }

      .app-shell__session:hover {
        background: rgba(255, 255, 255, 0.14);
        opacity: 1;
      }

      .app-shell__session:focus-visible {
        outline: none;
        box-shadow:
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
        opacity: 1;
      }

      .app-shell__session-icon {
        display: none;
      }

      /* BR-102: mobile swaps the squashed text label for a login/logout icon. */
      @media (max-width: 767px) {
        .app-shell__session {
          padding: 0 12px;
          min-width: 46px;
          justify-content: center;
        }

        .app-shell__session-label {
          display: none;
        }

        .app-shell__session-icon {
          display: inline-flex;
        }
      }

      .app-shell--puzzle-bar luna-status-chip:not([hidden]) {
        display: inline-flex;
      }

      .app-shell--picker-back luna-back-chip,
      .app-shell--puzzle-bar luna-back-chip {
        display: flex;
      }

      .app-shell__current-indicator {
        align-items: center;
        height: 46px;
        padding: 0 20px;
        border-radius: var(--luna-radius-btn-sm);
        background: var(--mbw-onbar-pill);
        color: var(--mbw-onbar-text);
      }

      @media (min-width: 768px) {
        .app-shell--home-nav luna-nav {
          display: flex;
        }
      }

      @media (max-width: 767px) {
        .app-shell--home-nav .app-shell__current-indicator {
          display: inline-flex;
        }
      }
    `;
  }
}
